import { useEffect, useState } from "react";
import {
    ListarTiposDocumentoResultado,
    CrearTipoDocumentoResultado,
    ActualizarTipoDocumentoResultado,
    EliminarTipoDocumentoResultado,
} from "../../../Service/Service.resultadosexamenes";
import usePermissions from "../../../hook/usePermissions";
import useListaLocal from "../../../hook/useListaLocal";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda } from "../../UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../../UI/Chip";
import { CampoTexto } from "../../UI/Campo";
import Boton from "../../UI/Boton";
import MenuAcciones from "../../UI/MenuAcciones";
import Modal from "../../UI/Modal/Modal";
import { useToast } from "../../UI/Aviso";
import { IconoMas, IconoEditar, IconoEliminar, IconoBuscar } from "../../UI/Iconos";

// RESULTADOS-05-ESTADO-PENDIENTE-TIPODOC-01: administración de los tipos de
// documento de resultados. Los tipos BASE del sistema (es_base) se muestran
// sin acciones destructivas; los PERSONALIZADOS de la compañía se pueden
// editar y desactivar/eliminar. El backend es la autoridad real del scope.
function TiposDocumentoResultado() {
    const { hasPermission } = usePermissions();
    const puedeGestionar = hasPermission("resultados.tipo_documento.manage");

    const [loading, setLoading] = useState(false);
    const [tipos, setTipos] = useState([]);
    const [error, setError] = useState(null);
    const [nuevoNombre, setNuevoNombre] = useState("");
    const [guardando, setGuardando] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [editNombre, setEditNombre] = useState("");
    const [porEliminar, setPorEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);
    const { mostrar } = useToast();
    const lista = useListaLocal(tipos, { campos: ["tipo", "estado"] });

    const cargar = async () => {
        setLoading(true);
        const res = await ListarTiposDocumentoResultado();
        if (res && res.success) {
            setTipos(res.data || []);
            setError(null);
        } else {
            // Antes un fallo dejaba la tabla vacía diciendo "Sin tipos de
            // documento", que es una respuesta distinta de "no se pudo cargar".
            setTipos([]);
            setError(res?.message || "No se pudieron obtener los tipos de documento.");
        }
        setLoading(false);
    };

    useEffect(() => {
        cargar();
    }, []);

    const esBase = (t) => t.es_base === true || t.company_id === null || t.company_id === undefined;

    const handleCrear = async (e) => {
        e.preventDefault();
        const nombre = nuevoNombre.trim();
        if (!nombre) return;
        setGuardando(true);
        const res = await CrearTipoDocumentoResultado(nombre);
        setGuardando(false);
        if (res && res.success) {
            setNuevoNombre("");
            await cargar();
        } else {
            mostrar({ tipo: "error", texto: res?.message || "No se pudo crear el tipo de documento." });
        }
    };

    const handleGuardarEdicion = async (id) => {
        const nombre = editNombre.trim();
        if (!nombre) return;
        const res = await ActualizarTipoDocumentoResultado(id, nombre);
        if (res && res.success) {
            setEditandoId(null);
            setEditNombre("");
            await cargar();
        } else {
            mostrar({ tipo: "error", texto: res?.message || "No se pudo actualizar el tipo de documento." });
        }
    };

    const handleEliminar = async (tipo) => {
        setEliminando(true);
        const res = await EliminarTipoDocumentoResultado(tipo.id);
        setEliminando(false);
        setPorEliminar(null);
        if (res && res.success) {
            mostrar({ tipo: "exito", texto: res.message || (res.desactivado ? "Desactivado" : "Eliminado") });
            await cargar();
        } else {
            mostrar({ tipo: "error", texto: res?.message || "No se pudo eliminar el tipo de documento." });
        }
    };

    const columnas = [
        {
            key: "tipo",
            titulo: "Nombre",
            render: (t) =>
                editandoId === t.id ? (
                    <CampoTexto
                        etiqueta="Nuevo nombre"
                        etiquetaOculta
                        value={editNombre}
                        onChange={(e) => setEditNombre(e.target.value)}
                        data-testid={`tipodoc-editar-${t.id}`}
                    />
                ) : (
                    <Celda valor={t.tipo} />
                ),
        },
        {
            key: "origen",
            titulo: "Tipo",
            ancho: 160,
            render: (t) => <ChipCategoria tono={esBase(t) ? "neutro" : "info"}>{esBase(t) ? "Base" : "Personalizado"}</ChipCategoria>,
        },
        {
            key: "estado",
            titulo: "Estado",
            ancho: 130,
            render: (t) => <ChipEstado tono={t.estado === "Activo" ? "hecho" : "anulado"}>{t.estado}</ChipEstado>,
        },
        {
            key: "acciones",
            titulo: "",
            ancho: 190,
            alinear: "derecha",
            render: (t) => {
                // Los tipos BASE del sistema no se tocan, y sin el permiso
                // tampoco: exactamente igual que antes.
                if (esBase(t) || !puedeGestionar) return <Celda valor="—" />;
                if (editandoId === t.id) {
                    return (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                            <Boton
                                tamano="sm"
                                variante="primario"
                                onClick={() => handleGuardarEdicion(t.id)}
                                disabled={!editNombre.trim()}
                                data-testid={`tipodoc-guardar-${t.id}`}
                            >
                                Guardar
                            </Boton>
                            <Boton
                                tamano="sm"
                                onClick={() => {
                                    setEditandoId(null);
                                    setEditNombre("");
                                }}
                                data-testid={`tipodoc-cancelar-${t.id}`}
                            >
                                Cancelar
                            </Boton>
                        </span>
                    );
                }
                return (
                    <MenuAcciones
                        titulo={`Acciones de ${t.tipo}`}
                        acciones={[
                            {
                                label: "Editar",
                                icono: IconoEditar,
                                onClick: () => {
                                    setEditandoId(t.id);
                                    setEditNombre(t.tipo);
                                },
                            },
                            {
                                label: "Desactivar",
                                icono: IconoEliminar,
                                peligro: true,
                                onClick: () => setPorEliminar(t),
                            },
                        ]}
                    />
                );
            },
        },
    ];

    return (
        <div>
            {puedeGestionar && (
                <form className="d-flex gap-2 align-items-end mb-3" onSubmit={handleCrear}>
                    <div style={{ maxWidth: 360, width: "100%" }}>
                        <CampoTexto
                            etiqueta="Nombre del nuevo tipo"
                            value={nuevoNombre}
                            onChange={(e) => setNuevoNombre(e.target.value)}
                            placeholder="Nombre del nuevo tipo"
                            data-testid="tipodoc-nuevo"
                        />
                    </div>
                    <Boton
                        type="submit"
                        variante="primario"
                        icono={IconoMas}
                        cargando={guardando}
                        disabled={!nuevoNombre.trim()}
                        data-testid="tipodoc-crear"
                    >
                        Nuevo
                    </Boton>
                </form>
            )}

            <div className="mb-3" style={{ maxWidth: 380 }}>
              <CampoTexto
                etiqueta="Buscar tipo de documento"
                icono={IconoBuscar}
                placeholder="Nombre"
                value={lista.texto}
                onChange={(e) => lista.setTexto(e.target.value)}
                data-testid="tipodoc-buscar"
              />
            </div>

            <DataTable
                columnas={columnas}
                filas={lista.visibles}
                filaKey="id"
                cargando={loading}
                error={error}
                onReintentar={cargar}
                etiquetaTabla="Tipos de documento de resultados"
                hayFiltrosAplicados={lista.hayBusqueda}
                onLimpiarFiltros={lista.limpiar}
                textoVacioSinFiltros="Sin tipos de documento."
                paginacion={lista.paginacion}
            />

            <Modal
                abierto={Boolean(porEliminar)}
                titulo={`Eliminar "${porEliminar?.tipo || ""}"`}
                destructivo
                onCerrar={() => setPorEliminar(null)}
                secundario={{ label: "Cancelar" }}
                primario={{ label: "Sí, continuar", cargando: eliminando, onClick: () => handleEliminar(porEliminar) }}
            >
                <p>Si el tipo ya se usó en resultados, se desactivará en lugar de eliminarse.</p>
            </Modal>
        </div>
    );
}

export default TiposDocumentoResultado;
